import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

export interface ReportAuditParams {
  companyId: string;
  reportType: string;
  filters: Record<string, any>;
  status: 'pending' | 'success' | 'error';
  errorMessage?: string;
  durationMs?: number;
  pdfSizeBytes?: number;
  rowsProcessed?: number;
}

/**
 * Hook para auditoria de geração de relatórios.
 * Registra cada geração de relatório na tabela fleet_report_runs.
 */
export function useReportAudit() {
  const { profile } = useAuth();

  const logReportRun = useCallback(async (params: ReportAuditParams) => {
    const userId = profile?.id;
    
    if (!userId) {
      console.warn('[ReportAudit] Usuário não autenticado, log não registrado');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('fleet_report_runs')
        .insert({
          company_id: params.companyId,
          user_id: userId,
          report_type: params.reportType,
          filters_applied: params.filters,
          status: params.status,
          error_message: params.errorMessage,
          duration_ms: params.durationMs,
          pdf_size_bytes: params.pdfSizeBytes,
          rows_processed: params.rowsProcessed
        })
        .select('id')
        .single();

      if (error) {
        console.error('[ReportAudit] Erro ao registrar log:', error);
        return null;
      }

      return data?.id;
    } catch (err) {
      console.error('[ReportAudit] Exceção ao registrar log:', err);
      return null;
    }
  }, [profile?.id]);

  /**
   * Cria um log pendente e retorna função para atualizar status
   */
  const startReportRun = useCallback(async (
    companyId: string,
    reportType: string,
    filters: Record<string, any>
  ) => {
    const startTime = Date.now();
    
    const logId = await logReportRun({
      companyId,
      reportType,
      filters,
      status: 'pending'
    });

    return {
      logId,
      startTime,
      
      async complete(params: {
        status: 'success' | 'error';
        errorMessage?: string;
        pdfSizeBytes?: number;
        rowsProcessed?: number;
      }) {
        if (!logId) return;
        
        const durationMs = Date.now() - startTime;
        
        try {
          await supabase
            .from('fleet_report_runs')
            .update({
              status: params.status,
              error_message: params.errorMessage,
              duration_ms: durationMs,
              pdf_size_bytes: params.pdfSizeBytes,
              rows_processed: params.rowsProcessed
            })
            .eq('id', logId);
        } catch (err) {
          console.error('[ReportAudit] Erro ao atualizar log:', err);
        }
      }
    };
  }, [logReportRun]);

  return {
    logReportRun,
    startReportRun
  };
}
